import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import scribe from '@grab-x/scribe-web-sdk';
import {
  CAMPAIGN_TYPES,
  PROJECT_OBJECTIVE_COLUMNS_MAPPING,
} from 'omega-rtc/constants/ucd';
import { CAMPAIGN_TYPE_KEY } from 'omega-rtc/constants/metrics';
import './UCDHomePage.scss';
import './CampaignDetail.scss';
import ProgressiveContainer from 'omega/components/container/ProgressiveContainer';
import ScribeEventName, { ScribeExtras } from 'omega-rtc/constants/scribe';
import { getRequestStateKey, getTimeRanges } from 'omega-rtc/utils/ucd-utils';
import {
  GET_TRIDENT_CAMPAIGN,
  GET_UCD_CAMPAIGN_METRICS,
  GET_UCD_CAMPAIGN_METRICS_BENCHMARK,
  getTridentCampaign,
  getJourneyCampaignBusinessMetrics,
  getUCDCampaignMetrics,
  getUCDCampaignMetricsByDate,
  getUCDCampaignMetricsBenchmark,
  getJourneyCampaignProgress,
} from 'omega-rtc/actions/ucd';
import TridentMetricsPage from 'omega-rtc/views/ucd/TridentMetricsPage';
import JourneyMetricsPage from 'omega-rtc/views/ucd/JourneyMetricsPage';
import MetricsPage from 'omega-rtc/views/ucd/MetricsPage';
import NoMetrics from 'omega-rtc/views/ucd/NoMetrics';
import {
  hasJustSuccess,
  isRequestsPending,
} from 'omega-rtc/utils/request-state-utils';

const EARLIEST_METRICS_AVAILABLE_DATE = '2022-06-01';

const PerformancePage = () => {
  const dispatch = useDispatch();

  const campaignView = useSelector((state) => state.ucd.campaignView);
  const projectView = useSelector((state) => state.ucd.projectView);
  const { subObjective } = projectView.project;
  const { objective, campaign } = campaignView;
  const campaignMetricsView = useSelector(
    (state) => state.ucd.campaignMetricsView,
  );
  const campaignMetricsBenchmark = useSelector(
    (state) => state.ucd.campaignMetricsBenchmark,
  );
  const campaignMetricsViewByDate = useSelector(
    (state) => state.ucd.campaignMetricsViewByDate,
  );
  const tridentCampaignView = useSelector(
    (state) => state.ucd.tridentCampaignView,
  );
  const journeyCampaignBusinessMetricsView = useSelector(
    (state) => state.ucd.journeyCampaignBusinessMetricsView,
  );
  const journeyCampaignProgressView = useSelector(
    (state) => state.ucd.journeyCampaignProgressView,
  );
  const getCampaignMetricsViewRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(GET_UCD_CAMPAIGN_METRICS), {}),
  );
  const getCampaignMetricsBenchmarkRequest = useSelector((state) =>
    _.get(
      state.requests,
      getRequestStateKey(GET_UCD_CAMPAIGN_METRICS_BENCHMARK),
      {},
    ),
  );
  const getTridentCampaignRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(GET_TRIDENT_CAMPAIGN), {}),
  );

  useEffect(() => {
    const campaignID = campaignView.campaignID;
    const projectID = projectView.projectID;

    if (_.get(campaignView, 'campaign.templateType') === 'multi-step-journey') {
      const startTime = _.get(campaignView, 'campaign.startTime');
      const endTime = _.get(campaignView, 'campaign.endTime');
      dispatch(
        getUCDCampaignMetrics(projectID, campaignID, { startTime, endTime }),
      );
    } else {
      dispatch(getUCDCampaignMetrics(projectID, campaignID));
    }

    dispatch(getUCDCampaignMetricsBenchmark(projectID, campaignID));

    scribe.sendEvent(ScribeEventName.CAMPAIGN_METRICS, {
      campaignId: campaignID,
      ...ScribeExtras.OPEN_PAGE,
    });
  }, [campaignView, projectView]);

  useEffect(() => {
    const prev = getCampaignMetricsViewRequest;
    const current = getCampaignMetricsViewRequest;

    const campaignID = campaignView.campaignID;
    const projectID = projectView.projectID;

    if (hasJustSuccess(prev, current)) {
      const campaignType = _.get(campaignMetricsView, CAMPAIGN_TYPE_KEY);
      if (campaignType === CAMPAIGN_TYPES.journey) {
        dispatch(getTridentCampaign(projectID, campaignID));
        dispatch(getJourneyCampaignBusinessMetrics(projectID, campaignID));
        dispatch(
          getJourneyCampaignProgress(
            campaignView.countryID,
            campaignView.campaign.mamCampaignID,
          ),
        );
      }
    }

    if (hasJustSuccess(getTridentCampaignRequest, getTridentCampaignRequest)) {
      const frequency = _.get(
        tridentCampaignView,
        'campaign.nodes[1].data.schedule.frequency',
        '',
      );
      const startTime = _.get(
        tridentCampaignView,
        'campaign.nodes[1].data.schedule.startTime',
        '',
      );

      const timeRanges = getTimeRanges(startTime, frequency);
      _.each(timeRanges, (timeRange) => {
        dispatch(
          getUCDCampaignMetricsByDate(projectID, campaignID, {
            startTime: timeRange.startTime.format('YYYY-MM-DDTHH:mm:ss[Z]'),
            endTime: timeRange.endTime.format('YYYY-MM-DDTHH:mm:ss[Z]'),
          }),
        );
      });
    }
  }, [
    getCampaignMetricsViewRequest,
    campaignView,
    campaignMetricsView,
    projectView,
    getTridentCampaignRequest,
    tridentCampaignView,
  ]);

  const campaignBizMetricsColumns = React.useMemo(
    () => _.get(PROJECT_OBJECTIVE_COLUMNS_MAPPING, objective, []),
    [objective],
  );
  const isLoadingMetrics = React.useMemo(
    () => isRequestsPending(getCampaignMetricsViewRequest),
    [getCampaignMetricsViewRequest],
  );
  const isLoadingBenchmark = React.useMemo(
    () => isRequestsPending(getCampaignMetricsBenchmarkRequest),
    [getCampaignMetricsBenchmarkRequest],
  );

  const isCampaignBeforeEarliestDate = React.useMemo(
    () => _.get(campaign, 'start') < EARLIEST_METRICS_AVAILABLE_DATE,
    [campaign],
  );
  const { headerText, bodyText } = React.useMemo(() => {
    if (isCampaignBeforeEarliestDate) {
      return {
        headerText: 'Metrics Not Available',
        bodyText:
          'Performance metrics will not be available for campaigns that start before 1 June 2022',
      };
    }
    return { headerText: '', bodyText: '' };
  }, [isCampaignBeforeEarliestDate]);

  const businessMetrics = React.useMemo(
    () => _.get(campaignMetricsView, 'business', {}),
    [campaignMetricsView],
  );
  const commsMetrics = React.useMemo(
    () => _.get(campaignMetricsView, 'comms', {}),
    [campaignMetricsView],
  );
  const campaignType = React.useMemo(
    () => _.get(campaignMetricsView, CAMPAIGN_TYPE_KEY),
    [campaignMetricsView],
  );

  const RenderMetricsPage = () => {
    switch (campaignType) {
      case CAMPAIGN_TYPES.trident:
        return (
          <TridentMetricsPage
            projectView={projectView}
            campaignView={campaignView}
            campaignMetricsView={campaignMetricsView}
            businessMetrics={businessMetrics}
            campaignBizMetricsColumns={campaignBizMetricsColumns}
            subObjective={subObjective}
            isLoadingBenchmark={isLoadingBenchmark}
            metricsBenchmark={campaignMetricsBenchmark}
          />
        );
      case CAMPAIGN_TYPES.journey:
        return (
          <JourneyMetricsPage
            projectView={projectView}
            campaignView={campaignView}
            tridentCampaignView={tridentCampaignView}
            campaignMetricsViewByDate={campaignMetricsViewByDate}
            businessMetrics={businessMetrics}
            campaignBizMetricsColumns={campaignBizMetricsColumns}
            subObjective={subObjective}
            isLoadingBenchmark={isLoadingBenchmark}
            metricsBenchmark={campaignMetricsBenchmark}
            journeyCampaignBusinessMetricsView={
              journeyCampaignBusinessMetricsView
            }
            journeyCampaignProgressView={journeyCampaignProgressView}
          />
        );
      default:
        return (
          <MetricsPage
            campaignView={campaignView}
            campaignMetricsView={campaignMetricsView}
            businessMetrics={businessMetrics}
            campaignBizMetricsColumns={campaignBizMetricsColumns}
            subObjective={subObjective}
          />
        );
    }
  };

  return (
    <ProgressiveContainer isLoading={isLoadingMetrics}>
      {!_.isEmpty(businessMetrics) || !_.isEmpty(commsMetrics) ? (
        <RenderMetricsPage />
      ) : (
        <NoMetrics headerText={headerText} bodyText={bodyText} />
      )}
    </ProgressiveContainer>
  );
};

export default PerformancePage;
